import React, { useState } from 'react';
import { Card, Typography, DatePicker, Radio, Space, Tabs, Grid, ConfigProvider, Tooltip } from 'antd';
import {
  AreaChartOutlined,
  FallOutlined,
  BarChartOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import LayoutHome from '../../components/layout/LayoutHome';
import SeccionFlujoCaja from './sections/SeccionFlujoCaja';
import SeccionGastos from './sections/SeccionGastos';
import SeccionVentas from './sections/SeccionVentas';
import SeccionDashboard from './sections/SeccionDashboard';
import SeccionPersonal from './sections/SeccionPersonal';
import dayjs from 'dayjs';
import { usePermission } from '../../hooks/usePermission';
import es_ES from 'antd/locale/es_ES';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Informes = () => {

  const { hasPermission } = usePermission();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [fechas, setFechas] = useState([dayjs().startOf('month'), dayjs().endOf('day')]);

  const aplicarFiltroRapido = (e) => {
    const tipo = e.target.value;
    const hoy = dayjs();
    if (tipo === 'hoy') {
      setFechas([hoy.startOf('day'), hoy.endOf('day')]);
    } else if (tipo === 'semana') {
      setFechas([hoy.startOf('week'), hoy.endOf('day')]);
    } else if (tipo === 'mes') {
      setFechas([hoy.startOf('month'), hoy.endOf('day')]);
    }
  };

  const desdeStr = fechas[0].format('YYYY-MM-DD');
  const hastaStr = fechas[1].format('YYYY-MM-DD');

  // --- ESTRUCTURA ERP CON TOOLTIPS DE AYUDA ---
  const tabItems = [
    hasPermission('informes:resumen_general') ? {
      key: 'financiero',
      label: (
        <Tooltip title="Resumen global de las ganancias, gastos y utilidad real del negocio." color="#0f172a">
          <span style={{ fontWeight: '600', fontSize: '14px' }}><AreaChartOutlined /> Financiero y Operativo</span>
        </Tooltip>
      ),
      children: <SeccionDashboard desde={desdeStr} hasta={hastaStr} />,
    } : null,

    hasPermission('informes:ver_ventas') ? {
      key: 'ventas',
      label: (
        <Tooltip title="Análisis detallado de los productos más vendidos y los métodos de pago usados por los clientes." color="#0f172a">
          <span style={{ fontWeight: '600', fontSize: '14px' }}><BarChartOutlined /> Análisis de Ventas</span>
        </Tooltip>
      ),
      children: <SeccionVentas desde={desdeStr} hasta={hastaStr} />,
    } : null,

    hasPermission('informes:ver_ventas') ? {
      key: 'personal',
      label: (
        <Tooltip title="Mide el desempeño, las ventas y el rendimiento individual de cada uno de tus empleados." color="#0f172a">
          <span style={{ fontWeight: '600', fontSize: '14px' }}><TeamOutlined /> Rendimiento Personal</span>
        </Tooltip>
      ),
      children: <SeccionPersonal desde={desdeStr} hasta={hastaStr} />,
    } : null,

    hasPermission('informes:ver_gastos') ? {
      key: 'gastos',
      label: (
        <Tooltip title="Control y registro de todo el dinero que sale del negocio (servicios, nómina, compras)." color="#0f172a">
          <span style={{ fontWeight: '600', fontSize: '14px' }}><FallOutlined /> Control de Egresos</span>
        </Tooltip>
      ),
      children: <SeccionGastos desde={desdeStr} hasta={hastaStr} />,
    } : null,

    {
      key: 'auditoria_caja',
      label: (
        <Tooltip title="Historial para vigilar los cuadres, faltantes, sobrantes y movimientos de dinero en efectivo." color="#0f172a">
          <span style={{ fontWeight: '600', fontSize: '14px' }}><SafetyCertificateOutlined /> Auditoría de Caja</span>
        </Tooltip>
      ),
      children: <SeccionFlujoCaja desde={desdeStr} hasta={hastaStr} />,
    }
  ].filter(Boolean);

  const colorearRango = (current, info) => {
    if (info.type !== 'date') return info.originNode;

    const diaActual = current.startOf('day');
    const inicio = fechas[0].startOf('day');
    const fin = fechas[1].startOf('day');

    const esInicio = diaActual.isSame(inicio);
    const esFin = diaActual.isSame(fin);
    const estaEnMedio = diaActual.isAfter(inicio) && diaActual.isBefore(fin);

    // Si el día no es parte de nuestro filtro, lo renderiza normal
    if (!esInicio && !esFin && !estaEnMedio) {
      return info.originNode;
    }

    // Colores basados en tu tema (#22BABB y celeste suave #e0f2fe)
    return (
      <div
        className="ant-picker-cell-inner"
        style={{
          background: estaEnMedio ? '#e0f2fe' : '#22BABB',
          color: (esInicio || esFin) ? '#fff' : 'inherit',
          borderRadius: esInicio && esFin ? '6px' : (esInicio ? '6px 0 0 6px' : (esFin ? '0 6px 6px 0' : '0')),
          width: '100%',
          margin: 0
        }}
      >
        {current.date()}
      </div>
    );
  };

  return (
    <ConfigProvider locale={es_ES}>
      <LayoutHome pageSeleccionada="informes">
        <div style={{ padding: isMobile ? '8px' : '12px 16px', maxWidth: '1600px', margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* BARRA SUPERIOR COMPACTA (TÍTULO + FILTROS) */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: '12px',
            marginBottom: '12px',
            background: '#fff',
            padding: isMobile ? '10px' : '8px 16px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            flexShrink: 0
          }}>

            {/* TÍTULO COMPACTO Y AMIGABLE */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ background: '#e0f2fe', padding: '6px', borderRadius: '8px' }}>
                <AreaChartOutlined style={{ fontSize: '20px', color: '#22BABB' }} />
              </div>
              <Title level={4} style={{ margin: 0, color: '#0f172a', letterSpacing: '-0.5px', display: 'flex', alignItems: 'center' }}>
                Reportes y Estadísticas
                <Tooltip title="Aquí encontrarás toda la información financiera de tu negocio para tomar mejores decisiones." color="#22BABB">
                  <InfoCircleOutlined style={{ fontSize: '16px', color: '#94a3b8', marginLeft: '8px', cursor: 'help' }} />
                </Tooltip>
              </Title>
            </div>

            {/* FILTROS COMPACTOS EN LÍNEA */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <Space direction="vertical" size={2}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>{isMobile ? '' : 'Filtros por día, semana o mes'}</Text>
                <Radio.Group
                  defaultValue="mes"
                  onChange={aplicarFiltroRapido}
                  style={{ display: 'flex' }}
                  size={isMobile ? "middle" : "middle"}
                >
                  <Radio.Button value="hoy">Hoy</Radio.Button>
                  <Radio.Button value="semana">Semana</Radio.Button>
                  <Radio.Button value="mes">Mes</Radio.Button>
                </Radio.Group>
              </Space>

              <Space size={4}>
                <Space direction="vertical" size={2}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Desde</Text>
                  <DatePicker
                    style={{ width: isMobile ? '110px' : '130px' }}
                    value={fechas[0]}
                    onChange={(val) => setFechas([val ? val.startOf('day') : dayjs().startOf('day'), fechas[1]])}
                    disabledDate={(current) => current && current > fechas[1]}
                    format="DD/MM/YYYY"
                    allowClear={false}
                    cellRender={colorearRango}
                  />
                </Space>
                <Text type="secondary" style={{ margin: '0 4px', marginTop: '18px' }}>-</Text>
                <Space direction="vertical" size={2}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Hasta</Text>
                  <DatePicker
                    style={{ width: isMobile ? '110px' : '130px' }}
                    value={fechas[1]}
                    onChange={(val) => setFechas([fechas[0], val ? val.endOf('day') : dayjs().endOf('day')])}
                    disabledDate={(current) => current && current < fechas[0].startOf('day')}
                    format="DD/MM/YYYY"
                    allowClear={false}
                    cellRender={colorearRango}
                  />
                </Space>
              </Space>
            </div>
          </div>

          {/* CONTENEDOR DE PESTAÑAS (Márgenes reducidos) */}
          <Card
            bordered={false}
            style={{ borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', flex: 1 }}
            styles={{ body: { padding: isMobile ? '8px' : '8px 16px 16px 16px' } }}
          >
            <Tabs
              defaultActiveKey="financiero"
              items={tabItems}
              size="middle"
              tabBarStyle={{ marginBottom: '16px', borderBottom: '1px solid #f1f5f9' }}
            />
          </Card>

        </div>
      </LayoutHome>
    </ConfigProvider>
  );
};

export default Informes;